/**
 * Age-group eligibility helpers — shared by competitions and events, and the
 * ONE copy both the client and the Functions run.
 *
 * Cohort model (decided product rule): a competitor's category is the age
 * they reach in the YEAR of the competition/event, NOT their exact age on
 * the day. This matches IFSC / general sport practice and the existing
 * `birthYear` field used throughout the codebase.
 *
 *   cohortAge = (year of event/competition start date) - birthYear
 *
 * A group's `ageMin` / `ageMax` are inclusive bounds; either may be null
 * (unbounded → open group on that side). A group with both null has no age
 * restriction at all and is open to everyone, even users with no birthYear.
 *
 * WHY IT IS SHARED. Both sides answer one question: may this person enter
 * this group? The client decides whether the registration button is offered;
 * the callable decides whether the write is accepted. With two copies, drift
 * was a one-way trap — the app offering an age group the server then refuses
 * with no wording anywhere to explain why, or, worse, the server accepting an
 * entry the rules meant to exclude, discovered at the ceremony. One copy
 * removes that by construction.
 */

/** Resolve a reference YEAR from a `YYYY-MM-DD` string, Date, or year number. */
export function refYearOf(dateOrYear) {
  if (dateOrYear == null || dateOrYear === "") return new Date().getFullYear();
  if (typeof dateOrYear === "number" && Number.isFinite(dateOrYear)) {
    return Math.trunc(dateOrYear);
  }
  const s = String(dateOrYear);
  const m = s.match(/^(\d{4})/);
  if (m) return Number(m[1]);
  const d = new Date(dateOrYear);
  return Number.isNaN(d.getTime()) ? new Date().getFullYear() : d.getFullYear();
}

/** Normalize an ageMin/ageMax cell to a finite integer or null. */
function bound(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

/** Cohort age for a birth year at a given reference date/year, or null if birthYear is missing/invalid. */
export function cohortAge(birthYear, dateOrYear) {
  const by = Number(birthYear);
  if (!Number.isInteger(by) || by <= 0) return null;
  return refYearOf(dateOrYear) - by;
}

/** True if the group constrains age on at least one side. */
export function isGroupAgeRestricted(group) {
  if (!group) return false;
  return bound(group.ageMin) != null || bound(group.ageMax) != null;
}

/**
 * Eligibility reason code, or null when eligible.
 * Codes: "birthYearMissing" | "tooYoung" | "tooOld".
 * Unrestricted groups are always eligible (returns null) regardless of birthYear.
 */
export function ageEligibilityReason(group, birthYear, dateOrYear) {
  if (!isGroupAgeRestricted(group)) return null;
  const age = cohortAge(birthYear, dateOrYear);
  if (age == null) return "birthYearMissing";
  const min = bound(group.ageMin);
  const max = bound(group.ageMax);
  if (min != null && age < min) return "tooYoung";
  if (max != null && age > max) return "tooOld";
  return null;
}

/** Convenience boolean wrapper around {@link ageEligibilityReason}. */
export function isAgeEligible(group, birthYear, dateOrYear) {
  return ageEligibilityReason(group, birthYear, dateOrYear) == null;
}

/** Age of legal adulthood. Not configurable: it is a legal fact, not a gym policy. */
export const ADULT_AGE = 18;

/**
 * Is this person a minor at the event/competition? Cohort rule, like every
 * other age answer here: the age reached in the START YEAR, not on the day.
 *
 * Returns `null` — NOT `false` — when the birth year is missing or unusable.
 * "We do not know yet" and "confirmed adult" must not collapse into one value:
 * a caller that treats the unknown as an adult stops asking for the guardian's
 * details of a child whose birth year simply has not been typed in yet.
 */
export function isMinorAt(birthYear, dateOrYear) {
  const age = cohortAge(birthYear, dateOrYear);
  return age == null ? null : age < ADULT_AGE;
}

/**
 * Compact, language-neutral age-range label for a group chip.
 * Examples: "8–12", "≤14", "≥10", "12" (min===max), "" (unrestricted).
 *
 * Only the client renders it today; it lives here rather than in a
 * client-only leftover because it is pure, reads the same `bound` rule as
 * the eligibility check, and a server-rendered label (e-mail, PDF) would
 * otherwise grow a second copy.
 */
export function formatAgeRange(group) {
  const min = bound(group && group.ageMin);
  const max = bound(group && group.ageMax);
  if (min != null && max != null) return min === max ? String(min) : `${min}–${max}`;
  if (max != null) return `≤${max}`;
  if (min != null) return `≥${min}`;
  return "";
}
